import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { format, isValid, parseISO } from 'date-fns';
import { TZDate } from '@date-fns/tz';

@Injectable({
  providedIn: 'root',
})
export class ExportService {
  private renderer: Renderer2;
  constructor(rendererFactory: RendererFactory2) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  /**
   * CSVデータをエクスポートし、ダウンロードを開始する
   * @param csvColumnMapList CSVのカラムマップ
   * @param csvDataObjList CSVに変換するデータのリスト
   * @param fileName ダウンロードするファイル名
   * @param isBombed BOM付にするかどうかのフラグ
   * @param dateFormat 日付のフォーマット
   * @param srcZone インプットのタイムゾーン
   * @param dstZone タイムゾーンの変更先
   * @param excludeKeys 変換の対象外とするキーのリスト
   */
  public exportToCsv(
    csvColumnMapList: { key: string; label: string }[],
    csvDataObjList: unknown[],
    fileName: string,
    isBombed: boolean = true,
    dateFormat: string = "yyyy-MM-dd'T'HH:mm:ssXXX",
    srcZone: string = '',
    dstZone: string = 'Asia/Tokyo',
    excludeKeys: string[] = [],
  ): void {
    const csvData = this.convertToCsv(
      csvColumnMapList,
      csvDataObjList,
      dateFormat,
      srcZone,
      dstZone,
      excludeKeys,
    );
    // CSVデータをUTF-8形式でエンコードし、Blobオブジェクトを生成してダウンロード用のURLを作成する
    // isBombedがFalseであれば、BOMなしでCSVを作成する。
    const utf8Array = new TextEncoder().encode(
      isBombed ? '\uFEFF' + csvData : csvData,
    );
    const blob = new Blob([utf8Array], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = window.URL.createObjectURL(blob);

    // CSVファイルのダウンロードを開始するためのアンカー要素を作成し、クリックしてダウンロードを実行する
    const anchorElement = this.renderer.createElement('a');
    this.renderer.setAttribute(anchorElement, 'href', url);
    this.renderer.setAttribute(anchorElement, 'download', fileName);

    this.renderer.appendChild(document.body, anchorElement);
    anchorElement.click();
    this.renderer.removeChild(document.body, anchorElement);
    window.URL.revokeObjectURL(url);
  }

  /**
   * データをCSV形式の文字列に変換する
   * @param {Array<{ key: string; label: string }>} csvColumnMapList - CSVのカラムマップ
   * @param {unknown[]} csvDataObjList - CSVに変換するデータのリスト
   * @param {string} dateFormat - 日付のフォーマット
   * @param {string} srcZone - インプットのタイムゾーン
   * @param {string} dstZone - アウトプットの日付のタイムゾーン
   * @param {string[]} excludeKeys - 変換の対象外とするキーのリスト
   * @returns {string} CSV形式の文字列
   */
  private convertToCsv(
    csvColumnMapList: { key: string; label: string }[],
    csvDataObjList: unknown[],
    dateFormat: string,
    srcZone: string,
    dstZone: string,
    excludeKeys?: string[],
  ): string {
    const headers = csvColumnMapList.map((option) => option.label);

    const rows = csvDataObjList.map((info) => {
      const csvData = csvColumnMapList.reduce(
        (acc, option) => {
          const key = option.key;
          const value = (info as { [key: string]: unknown })[key];
          const isExcludedKey = excludeKeys?.includes(key);

          // Date型の場合は指定されたフォーマットで文字列に変換。文字列の場合はダブルクォートを付与しエスケープ処理。
          // その他の型（数値など）はそのまま文字列に変換。null、undefinedの場合は空文字とする。
          // 除外キーで指定された場合はそのまま文字列とする。
          if (isExcludedKey) {
            const stringValue =
              value === null || value === undefined ? '' : String(value);
            acc[key] = `"${stringValue.replace(/"/g, '""')}"`;
          } else if (value instanceof Date) {
            acc[key] = format(value, dateFormat);
          } else if (
            typeof value === 'string' &&
            value.trim() &&
            isValid(parseISO(value))
          ) {
            // ISO形式の文字列のみ日付として扱う
            // もしタイムゾーンが日時に含まれていればそのまま利用。
            // タイムゾーンが日時に含まれていないが指定されていた場合、パラメータを利用
            // いずれでもない場合、UTCで初期化
            let zonedDate = new TZDate(value);
            if (!this.hasValidTimeZone(value)) {
              if (srcZone.trim()) {
                zonedDate = new TZDate(value, srcZone);
              }
              zonedDate = new TZDate(value + '+00:00');
            }
            acc[key] = format(zonedDate.withTimeZone(dstZone), dateFormat);
          } else if (typeof value === 'string') {
            acc[key] = `"${value.replace(/"/g, '""')}"`;
          } else if (value != undefined) {
            acc[key] = String(value);
          } else if (value === null) {
            acc[key] = '';
          } else {
            acc[key] = '';
          }
          return acc;
        },
        {} as { [key: string]: string },
      );

      return csvColumnMapList.map((option) => csvData[option.key]).join(',');
    });

    return [headers.join(','), ...rows].join('\r\n') + '\r\n';
  }

  private hasValidTimeZone(dateString: string): boolean {
    try {
      // ISO 8601形式の日時を解析
      const date = parseISO(dateString);

      // Dateオブジェクトが有効かどうかを確認
      if (isNaN(date.getTime())) {
        return false;
      }

      // タイムゾーン情報が含まれているかを確認
      // 'Z'が含まれているか、または+/-で始まるタイムゾーンオフセットがあるか
      const timeZonePattern = /Z|[+-]\d{2}:\d{2}$/;
      return timeZonePattern.test(dateString);
    } catch (e) {
      return false;
    }
  }
}
